/**
 * MenuHeader — the shared PageHeader for the menu builder plus the menu
 * identity card (name, averages-menu state, active badge, menu-level comment).
 *
 * Per design rule §9.1 only שמירה is a one-tap action; every consequential
 * action (activate / duplicate / averages toggle / delete) sits behind a
 * labeled dropdown so a coach cannot fire it by mistake. ייצוא PDF and
 * שליחה ללקוח are deliberate disabled placeholders — later phases build them.
 */

import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  CaretDown,
  Check,
  Copy,
  DotsThreeVertical,
  FilePdf,
  FloppyDisk,
  ForkKnife,
  PaperPlaneTilt,
  Scales,
  Star,
  Trash,
} from "@phosphor-icons/react";

import { PageHeader } from "../../shared";
import HoverTip from "../../ui/HoverTip";
import Input from "../../ui/Input";
import "./MenuCards.css";
import "../../../pages/dashboard/nutrition/MenuBuilderPage.css";
import LoaderLoop from "../../shared/loader/LoaderLoop";

/** Every dropdown item explains itself on hover and on keyboard focus. */
const Tip = ({ text, children }) => (
  <HoverTip text={text} className="nmb-dropdown-tip" placement="bottom">
    {children}
  </HoverTip>
);

const MenuHeader = ({
  patientName,
  menuName,
  nameError,
  onMenuNameChange,
  comment,
  onCommentChange,
  isAveragesMenu,
  onToggleAverages,
  isActiveMenu,
  isNew,
  isDirty,
  isSaving,
  onSave,
  onSetActive,
  onDuplicate,
  onDelete,
  onExportPdf,
  isExportingPdf,
  onBack,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [menuOpen]);

  const runAndClose = (action) => () => {
    setMenuOpen(false);
    action();
  };

  const isMenuNameEmpty = !(menuName || "").trim();

  return (
    <>
      {onBack && (
        <button type="button" className="nmb-back-btn" onClick={onBack}>
          <ArrowRight size={16} weight="bold" />
          <span>לתפריטים של הלקוח/ה</span>
        </button>
      )}

      <PageHeader
        icon={ForkKnife}
        title={isNew ? "תפריט חדש" : "עריכת תפריט"}
        subtitle={patientName ? `תפריט עבור ${patientName}` : "בניית תפריט תזונה"}
        actions={
          <div className="nmb-header-actions">
            <HoverTip text="שמירת התפריט. הלקוח/ה רואה רק את התפריט הפעיל, ורק אחרי שמירה">
            <button
              type="button"
              className="nmb-primary-btn"
              onClick={onSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <LoaderLoop size="xs" />
                  <span>שומר...</span>
                </>
              ) : (
                <>
                  <FloppyDisk size={15} weight="fill" />
                  <span>שמור</span>
                </>
              )}
            </button>
            </HoverTip>

            <div className="nmb-menu-wrap" ref={menuRef}>
              <button
                type="button"
                className={`nmb-secondary-btn ${menuOpen ? "open" : ""}`}
                onClick={() => setMenuOpen((open) => !open)}
                aria-expanded={menuOpen}
                aria-haspopup="menu"
              >
                <DotsThreeVertical size={15} weight="bold" />
                <span>פעולות נוספות</span>
                <CaretDown
                  size={12}
                  weight="bold"
                  className={`nmb-caret ${menuOpen ? "open" : ""}`}
                />
              </button>

              {menuOpen && (
                <div className="nmb-dropdown" role="menu">
                  <Tip
                    text={
                      isActiveMenu
                        ? "זהו התפריט שהלקוח/ה רואה בעמוד האישי וב-PDF"
                        : isNew
                          ? "שמרו את התפריט קודם, ואז אפשר להפוך אותו לפעיל"
                          : "התפריט הפעיל הוא זה שהלקוח/ה רואה בעמוד האישי וב-PDF. רק תפריט אחד פעיל בכל רגע"
                    }
                  >
                    <button
                      type="button"
                      role="menuitem"
                      className="nmb-dropdown-item"
                      onClick={runAndClose(onSetActive)}
                      disabled={isNew || isActiveMenu}
                    >
                      <span className="nmb-dropdown-icon mint">
                        <Star size={13} weight="fill" />
                      </span>
                      <span>{isActiveMenu ? "זהו התפריט הפעיל" : "הפוך לתפריט פעיל"}</span>
                      {isActiveMenu && <Check size={13} weight="bold" />}
                    </button>
                  </Tip>

                  <Tip text="התת-בלוקים הם אפשרויות חלופיות ולא ארוחות נוספות, ולכן סה״כ הערכים היומיים מחולק במספר התת-בלוקים. מתאים לתפריט שבו הלקוח/ה בוחר/ת אפשרות אחת מכל בלוק">
                    <button
                      type="button"
                      role="menuitem"
                      className="nmb-dropdown-item"
                      onClick={runAndClose(onToggleAverages)}
                    >
                      <span className="nmb-dropdown-icon alt">
                        <Scales size={13} weight="fill" />
                      </span>
                      <span>תפריט ממוצעים</span>
                      {isAveragesMenu && <Check size={13} weight="bold" />}
                    </button>
                  </Tip>

                  <Tip
                    text={
                      isNew
                        ? "שמרו את התפריט קודם, ואז אפשר לשכפל אותו"
                        : "יוצר עותק של התפריט הזה (לא פעיל) — נוח כבסיס לתפריט הבא"
                    }
                  >
                    <button
                      type="button"
                      role="menuitem"
                      className="nmb-dropdown-item"
                      onClick={runAndClose(onDuplicate)}
                      disabled={isNew}
                    >
                      <span className="nmb-dropdown-icon">
                        <Copy size={13} weight="fill" />
                      </span>
                      <span>שכפול התפריט</span>
                    </button>
                  </Tip>

                  <Tip text="בקרוב: שליחת התפריט ללקוח/ה ב-SMS. בינתיים שולחים את הקישור לעמוד האישי — התפריט הפעיל מופיע שם">
                    <button
                      type="button"
                      role="menuitem"
                      className="nmb-dropdown-item"
                      disabled
                    >
                      <span className="nmb-dropdown-icon">
                        <PaperPlaneTilt size={13} weight="fill" />
                      </span>
                      <span>שליחה ללקוח/ה</span>
                      <span className="nmb-badge nmb-badge-neutral">בקרוב</span>
                    </button>
                  </Tip>

                  {/* A PDF of an unsaved menu would not match what is
                      stored, and the server refuses a menu id it cannot find */}
                  <Tip
                    text={
                      isNew || isDirty
                        ? "יש לשמור את התפריט לפני הייצוא — ה-PDF נבנה מהגרסה השמורה"
                        : "ייצוא התפריט כקובץ PDF ממותג של הקליניקה, להדפסה או לשליחה"
                    }
                  >
                    <button
                      type="button"
                      role="menuitem"
                      className="nmb-dropdown-item"
                      onClick={runAndClose(onExportPdf)}
                      disabled={isNew || isDirty || isExportingPdf}
                    >
                      <span className="nmb-dropdown-icon">
                        <FilePdf size={13} weight="fill" />
                      </span>
                      <span>{isExportingPdf ? "מייצא..." : "ייצוא PDF"}</span>
                    </button>
                  </Tip>

                  <div className="nmb-dropdown-sep" />

                  {/* The active menu cannot be deleted — mark another one
                      active first. Refused here, not after a confirm. */}
                  <Tip
                    text={
                      isActiveMenu
                        ? "זהו התפריט הפעיל — כדי למחוק אותו יש לסמן קודם תפריט אחר כפעיל"
                        : isNew
                          ? "התפריט עדיין לא נשמר — אין מה למחוק"
                          : "מחיקת התפריט לצמיתות. תפריט שאינו פעיל בלבד"
                    }
                  >
                    <button
                      type="button"
                      role="menuitem"
                      className="nmb-dropdown-item danger"
                      onClick={runAndClose(onDelete)}
                      disabled={isNew || isActiveMenu}
                    >
                      <span className="nmb-dropdown-icon danger">
                        <Trash size={13} weight="fill" />
                      </span>
                      <span>מחיקת התפריט</span>
                    </button>
                  </Tip>
                  {isActiveMenu && (
                    <p className="nmb-dropdown-note">
                      זהו התפריט הפעיל — כדי למחוק אותו יש לסמן קודם תפריט אחר כפעיל.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        }
      />

      <div className="nmb-identity">
        <div className={`nmb-identity-row ${isMenuNameEmpty ? "is-empty" : ""}`}>
          <Input
            label="שם התפריט"
            name="menuName"
            value={menuName}
            onChange={(event) => onMenuNameChange(event.target.value)}
            placeholder="לדוגמה: תפריט 1800 קלוריות"
            error={nameError}
            required
          />

          <div className="nmb-identity-badges">
            {isActiveMenu && (
              <span className="nmb-badge nmb-badge-mint">
                <Star size={11} weight="fill" />
                תפריט פעיל
              </span>
            )}
            {isAveragesMenu && (
              <span className="nmb-badge nmb-badge-alt">תפריט ממוצעים</span>
            )}
            {isDirty && <span className="nmb-badge nmb-badge-warn">שינויים שלא נשמרו</span>}
          </div>
        </div>

        <label className="nmb-identity-comment">
          <span className="nmb-field-label">הערה כללית לתפריט</span>
          <textarea
            className="nmb-textarea"
            rows={2}
            value={comment}
            onChange={(event) => onCommentChange(event.target.value)}
            placeholder="הערה שתופיע בראש התפריט של הלקוח/ה"
          />
        </label>
      </div>
    </>
  );
};

export default MenuHeader;
